import * as Yup from "yup";
import { FormGroup } from "../../../components/form/models/models";

function calculatedYear(yearApplied: string | undefined): string[] {
  const options = [new Date().getFullYear().toString(), (new Date().getFullYear() + 1).toString()];
  if (yearApplied && !options.includes(yearApplied)) {
    options.unshift(yearApplied);
  }
  return options;
}

function hideAgeExemption(commencementDateString: string, dateOfBirthString: string): boolean {
  const commencementDate = new Date(commencementDateString);
  const dateOfBirth = new Date(dateOfBirthString);
  return commencementDate.getFullYear() - dateOfBirth.getFullYear() < 6;
}

export const childAttendanceForm: FormGroup = {
  type: "formGroup",
  fields: [
    {
      type: "field",
      key: "yearApplied",
      fieldType: "dropdown",
      label: "Kindergarten year KIS is being applied for:",
      expressionProps: {
        options: (formContext) => calculatedYear(formContext.yearApplied),
        placeholder: (formContext) => formContext.yearApplied ?? "Select a Kindergarten year KIS is being applied for",
      },
      validation: Yup.string().trim().required("A response is required"),
    },
    {
      type: "field",
      key: "dateChildsCommencement",
      fieldType: "calendar",
      label: "Date of child's commencement at the kindergarten program, in the year being applied for.",
      validation: Yup.date()
        .required("A response is required")
        .when("yearApplied", (yearApplied, schema) => {
          if (yearApplied != null && Array.isArray(yearApplied) && yearApplied.length === 1 && yearApplied[0] != null) {
            return schema.test("match-confirm", "The year must be the same as the Kindergarten year KIS is being applied for", function (year: any) {
              year = new Date(year).getFullYear();
              return year.toString() === yearApplied[0];
            });
          }
          return schema;
        }),
    },
    {
      type: "field",
      fieldType: "radio",
      key: "childKinderYearLevel",
      label: "In the kindergarten year KIS is being applied for, will the child be accessing:",
      validation: Yup.string().trim().required("A response is required"),
      props: {
        options: [
          {
            label: "Three-year-old kindergarten",
            value: 1,
          },
          {
            label: "Four-year-old kindergarten",
            value: 2,
          },
        ],
      },
    },
    {
      type: "field",
      fieldType: "radio",
      key: "childsSecondYearFourYearOldKinder",
      label: "Will this be the child's second year in a 4 year old funded kindergarten program?",
      hideExpression: (formContext, values, model) => model.childKinderYearLevel != 2,
      validation: Yup.string().trim().required("A response is required"),
      props: {
        options: [
          {
            label: "Yes",
            value: 1,
          },
          {
            label: "No",
            value: 2,
          },
        ],
      },
    },
    {
      type: "field",
      fieldType: "radio",
      key: "childsSecondYearThreeYearOldKinder",
      label: "Will this be the child's second year in a 3 year old funded kindergarten program?",
      hideExpression: (formContext, values, model) => model.childKinderYearLevel != 1,
      validation: Yup.string().trim().required("A response is required"),
      props: {
        options: [
          {
            label: "Yes",
            value: 1,
          },
          {
            label: "No",
            value: 2,
          },
        ],
      },
    },
    {
      type: "field",
      fieldType: "radio",
      key: "childAccessingESK",
      label: "Is the child accessing an Early Start Kindergarten (ESK) place?",
      hideExpression: (formContext, values, model) => model.childKinderYearLevel != 1,
      validation: Yup.string().trim().required("A response is required"),
      props: {
        options: [
          {
            label: "Yes",
            value: 1,
          },
          {
            label: "No",
            value: 2,
          },
        ],
      },
    },
    {
      type: "field",
      fieldType: "radio",
      key: "childTurn6WhileAttendingKindergarten",
      label: "This child will turn 6 while attending kindergarten. Please confirm an exemption from school has been submitted to the Department.",
      hideExpression: (formContext, values, model) =>
        model.dateChildsCommencement == null || hideAgeExemption(model.dateChildsCommencement, formContext.child.dateOfBirth),
      validation: Yup.string().trim().required("A response is required"),
      props: {
        options: [
          {
            label: "Yes",
            value: 1,
          },
          {
            label: "No",
            value: 2,
          },
        ],
      },
    },
    {
      type: "field",
      key: "advisory",
      fieldType: "advisory",
      props: {
        type: "warning",
      },
      hideExpression: (formContext, values, model) =>
        model.dateChildsCommencement == null || hideAgeExemption(model.dateChildsCommencement, formContext.child.dateOfBirth),
      label:
        "If a school exemption is required, the KIS application will not be assessed until a school exemption is submitted to the DE regional office.",
    },
    {
      type: "field",
      key: "fundedKindergartenHours",
      fieldType: "number",
      props: {
        isFloat: true,
      },
      label:
        "Please advise how many funded kindergarten hours your service will provide for this child per week. This should not exceed the number of funded hours the child is eligible for. For long day care, this should not be the full hours of attendance. It should only be funded kindergarten hours, excluding childcare hours.",
      validation: Yup.number()
        .required("A response is required")
        .min(1, "Please enter the funded hours per week – must be a number between 1-30.")
        .max(30, "Please enter the funded hours per week – must be a number between 1-30.")
        .test("digit", "Please enter a number", (value: any) => {
          return /^(?:\d+(\.\d{1,2})?|\.\d{1,2})$/.test(value);
        }),
    },
    {
      type: "field",
      key: "advisory",
      fieldType: "advisory",
      props: {
        type: "warning",
        Body: () => (
          <p className="list-none pl-2 m-0">
            The number of hours of funded kindergarten this child is eligible for is based on the roll-out of Pre-Prep in Victoria. You can find out
            more about the rollout and schedule{" "}
            <a href="https://www.vic.gov.au/node/30010#roll-out-schedule" target="_blank" title="Link opens in a new tab" rel="noreferrer">
              here
            </a>
          </p>
        ),
      },
      label: "",
    },
    {
      type: "field",
      key: "childPreviouslyAttendedECS",
      fieldType: "dropdown",
      label: "Has the child previously attended an early childhood service?",
      props: {
        placeholder: "Select an application type",
        options: [
          {
            label: "Yes, the child has attended this service",
            value: 1,
          },
          {
            label: "Yes, the child has attended a different service",
            value: 2,
          },
          {
            label: "No",
            value: 3,
          },
        ],
      },
      validation: Yup.string().trim().required("A response is required"), // Basic required validation
    },
    {
      type: "field",
      fieldType: "radio",
      key: "childEligibleApplicationPriorYear",
      label: "Did the child have an eligible KIS application during their prior year at the service?",
      hideExpression: (formContext, values, model) => model.childPreviouslyAttendedECS != 1,
      validation: Yup.string().trim().required("A response is required"),
      props: {
        options: [
          {
            label: "Yes",
            value: 1,
          },
          {
            label: "No",
            value: 2,
          },
        ],
      },
    },
    {
      type: "formGroup",
      key: "criteriaNotMeet",
      label: "What supports did the child's kindergarten group receive? Select all that apply.",
      hideExpression: (formContext, values, model) => model.childPreviouslyAttendedECS != 1 || model.childEligibleApplicationPriorYear != 1,
      test: [
        "oneSelected",
        "A response is required",
        (value) => value.specialistConsultancy || value.additionalAssistant || value.specialistTraining || value.minorBuildingModifications,
      ],
      props: {
        isRequired: true,
      },
      fields: [
        {
          type: "field",
          key: "specialistConsultancy",
          fieldType: "checkbox",
          label: "Specialist consultancy",
        },
        {
          type: "field",
          key: "additionalAssistant",
          fieldType: "checkbox",
          label: "Additional assistant",
        },
        {
          type: "field",
          key: "specialistTraining",
          fieldType: "checkbox",
          label: "Specialist training",
        },
        {
          type: "field",
          key: "minorBuildingModifications",
          fieldType: "checkbox",
          label: "Minor building modifications",
        },
      ],
    },
    {
      type: "field",
      key: "dateOfFirstProgramSupportGroup",
      fieldType: "calendar",
      label:
        "The KIS program requires that a Program Support Group is convened and that meetings are held throughout the year. The first meeting of the PSG should be before the child's first attendance at the service. Please advise the date of the first PSG meeting.",
      validation: Yup.date().required("A response is required"),
    },
    {
      type: "field",
      key: "advisory",
      fieldType: "advisory",
      props: {
        type: "warning",
        Body: () => (
          <p className="list-none pl-2 m-0">
            A PSG brings together key people to support the inclusion of children with disability, developmental delay, or complex medical needs, from
            the time of enrolment at kindergarten until they move to school. It is organised and run by the early childhood teacher.
            <br />
            Members of the PSG should include the ECT, the child’s parents/carers/guardians, an early childhood intervention professional, therapist
            or other professionals supporting the child, and other professionals supporting the kindergarten service, such as the Early Years Manager.{" "}
            <br />
            You can find out more about PSGs{" "}
            <a href="https://www.vic.gov.au/program-support-groups-kindergarten" target="_blank" title="Link opens in a new tab" rel="noreferrer">
              {" "}
              &lt;https://www.vic.gov.au/program-support-groups-kindergarten&gt;
            </a>
          </p>
        ),
      },
      label: "",
    },
  ],
};
